import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Status, Tooltip } from '@mm/components';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { Tooltip as AntTooltip } from 'antd';
import { ReactComponent as KlaviyoOrdersIcon } from 'assets/icons/klaviyo_orders.svg';
import { ReactComponent as KlaviyoRevenueIcon } from 'assets/icons/klaviyo_revenue.svg';
import { ReactComponent as KlaviyoRevenuePostCreateIcon } from 'assets/icons/klaviyo_revenue_post_create.svg';
import { ReactComponent as KlaviyoRevenuePostEmailIcon } from 'assets/icons/klaviyo_revenue_post_email.svg';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import Spinner from '../../../../../../../../common/components/Spinner';
import styles from './IntegrationRevenue.module.css';

export type RevenueType =
  | 'purchase_value'
  | 'purchase_amount'
  | 'revenue_post_create'
  | 'revenue_post_email';

type Props = {
  platformName: string;
  totalRevenue: number;
  totalOrders: number;
  totalRevenuePostCreate?: number;
  totalRevenuePostEmail?: number;
  facebookPageId: number;
  getRevenues: (pageId: number, force: boolean, skipBusy?: boolean) => void;
  withSubtext?: boolean;
  base?: boolean;
  advanced?: boolean;
  className?: string;
  css?: {
    revenueBox?: string;
    revenueBoxesWrapper?: string;
    revenueData?: string;
  };
  onClick?: (revenueType: RevenueType) => void;
  tooltipTitle?: string;
};

const revenueRanges: [number, string][] = [
  [1, ''],
  [1e3, ' K'],
  [1e6, ' M'],
  [1e9, ' B'],
  [1e12, ' T'],
];

const formattedRevenueValue = (revenue: number) => {
  let matchingRangeIndex = -1;
  for (const range of revenueRanges) {
    if (range[0] < revenue) {
      matchingRangeIndex += 1;
    } else {
      break;
    }
  }
  matchingRangeIndex = Math.min(
    Math.max(matchingRangeIndex, 0),
    revenueRanges.length - 1,
  );
  const range = revenueRanges[matchingRangeIndex];
  const fixedValue = (revenue / range[0]).toFixed(1);

  return fixedValue + range[1];
};

export const IntegrationRevenue = ({
  platformName,
  totalRevenue,
  totalOrders,
  totalRevenuePostCreate,
  totalRevenuePostEmail,
  facebookPageId,
  getRevenues,
  withSubtext = true,
  base = true,
  advanced = true,
  className,
  css,
  onClick,
  tooltipTitle,
}: Props) => {
  const [isLoading, setIsLoading] = useState(true);
  const { companyName = 'Customers.ai' } = useWhitelabelConfig();

  const renderTooltip = (text: string) => (
    <span className={styles.revenueTooltipContainer}>
      <Tooltip title={<div>{text}</div>} placement="bottom">
        <ExclamationCircleOutlined className={styles.revenueTooltipIcon} />
      </Tooltip>
    </span>
  );

  const withTooltip = children => (
    <AntTooltip title={tooltipTitle} placement="bottom" color="#595959">
      {children}
    </AntTooltip>
  );

  useEffect(() => {
    (async () => {
      await getRevenues(facebookPageId, false, true);
      setIsLoading(false);
    })();
  }, []);

  return (
    <div className={classNames(styles.revenueContainer, className)}>
      <div
        className={classNames(styles.revenueBoxes, css?.revenueBoxesWrapper)}
      >
        {base && (
          <>
            {withTooltip(
              <div
                className={classNames(
                  styles.revenueBox,
                  css?.revenueBox,
                  styles.revenueBoxAdvanced,
                )}
                onClick={() => onClick?.('purchase_value')}
              >
                {isLoading && (
                  <div className={styles.revenueSpinnerWrapper}>
                    <Spinner
                      show={true}
                      position="absolute"
                      backgroundColor="none"
                      size={30}
                    />
                  </div>
                )}
                <KlaviyoRevenueIcon className={styles.revenueDataIcon} />
                <div
                  className={classNames(styles.revenueData, css?.revenueData)}
                >
                  <span className={styles.revenueDataLabel}>
                    {companyName} Contact Purchase Value
                  </span>
                  <span className={styles.revenueDataValue}>
                    {totalRevenue === 0.0 ? (
                      <Status
                        status="Will appear here"
                        color="#1890FF"
                        css={{ statusIndicator: styles.revenueStatusIndicator }}
                        className={styles.revenueStatus}
                      />
                    ) : (
                      `$${formattedRevenueValue(totalRevenue)} `
                    )}
                    {renderTooltip(`Total revenue in ${platformName} for all contacts stored in
          ${companyName}`)}
                  </span>
                </div>
              </div>,
            )}
            {withTooltip(
              <div
                className={classNames(
                  styles.revenueBox,
                  css?.revenueBox,
                  styles.revenueBoxAdvanced,
                )}
                onClick={() => onClick?.('purchase_amount')}
              >
                {isLoading && (
                  <div className={styles.revenueSpinnerWrapper}>
                    <Spinner
                      show={true}
                      position="absolute"
                      backgroundColor="none"
                      size={30}
                    />
                  </div>
                )}
                <KlaviyoOrdersIcon className={styles.revenueDataIcon} />
                <div
                  className={classNames(styles.revenueData, css?.revenueData)}
                >
                  <span className={styles.revenueDataLabel}>
                    Number of Purchases
                  </span>
                  <span className={styles.revenueDataValue}>
                    {totalOrders === 0 ? (
                      <Status
                        status="Will appear here"
                        color="#1890FF"
                        css={{ statusIndicator: styles.revenueStatusIndicator }}
                        className={styles.revenueStatus}
                      />
                    ) : (
                      new Intl.NumberFormat().format(totalOrders)
                    )}
                    {renderTooltip(` Total purchases in ${platformName} for all contacts stored in
                  ${companyName}`)}
                  </span>
                </div>
              </div>,
            )}
          </>
        )}
        {advanced && (
          <>
            {withTooltip(
              <div
                className={classNames(
                  styles.revenueBox,
                  css?.revenueBox,
                  styles.revenueBoxAdvanced,
                )}
                onClick={() => onClick?.('revenue_post_create')}
              >
                {isLoading && (
                  <div className={styles.revenueSpinnerWrapper}>
                    <Spinner
                      show={true}
                      position="absolute"
                      backgroundColor="none"
                      size={30}
                    />
                  </div>
                )}
                <KlaviyoRevenuePostCreateIcon
                  className={styles.revenueDataIcon}
                />
                <div
                  className={classNames(styles.revenueData, css?.revenueData)}
                >
                  <span className={styles.revenueDataLabel}>
                    Revenue Post-Email Sync
                  </span>
                  <span className={styles.revenueDataValue}>
                    {!totalRevenuePostCreate ||
                    totalRevenuePostCreate === 0.0 ? (
                      <Status
                        status="Will appear here"
                        color="#1890FF"
                        css={{ statusIndicator: styles.revenueStatusIndicator }}
                        className={styles.revenueStatus}
                      />
                    ) : (
                      `$${formattedRevenueValue(totalRevenuePostCreate)}`
                    )}
                    {renderTooltip(
                      'Revenue accounted after the contact was synced',
                    )}
                  </span>
                </div>
              </div>,
            )}
            {withTooltip(
              <div
                className={classNames(
                  styles.revenueBox,
                  css?.revenueBox,
                  styles.revenueBoxAdvanced,
                )}
                onClick={() => onClick?.('revenue_post_email')}
              >
                {isLoading && (
                  <div className={styles.revenueSpinnerWrapper}>
                    <Spinner
                      show={true}
                      position="absolute"
                      backgroundColor="none"
                      size={30}
                    />
                  </div>
                )}
                <KlaviyoRevenuePostEmailIcon
                  className={styles.revenueDataIcon}
                />
                <div
                  className={classNames(styles.revenueData, css?.revenueData)}
                >
                  <span className={styles.revenueDataLabel}>
                    Revenue Post-Email Send
                  </span>
                  <span className={styles.revenueDataValue}>
                    {!totalRevenuePostEmail || totalRevenuePostEmail === 0.0 ? (
                      <Status
                        status="Will appear here"
                        color="#1890FF"
                        css={{ statusIndicator: styles.revenueStatusIndicator }}
                        className={styles.revenueStatus}
                      />
                    ) : (
                      `$${formattedRevenueValue(totalRevenuePostEmail)}`
                    )}
                    {renderTooltip(
                      'Revenue only after an email was sent post contact sync',
                    )}
                  </span>
                </div>
              </div>,
            )}
          </>
        )}
      </div>

      {withSubtext && (
        <div className={styles.revenueSubtext}>
          Revenue is calculated by checking the email contacts in our system
          against the orders in your Klaviyo account.
        </div>
      )}
    </div>
  );
};
